import CloudIcon from "@mui/icons-material/Cloud";
import {
  RecommendationTypeHandler,
  RecommendationAdapter,
  NormalizedRecommendation,
  ListItemContent,
} from "../types";
import AzureRecommendationDetailsDialog from "../../components/azure/AzureRecommendationDetailsDialog";
import {
  AzureRecommendationSummaryDto,
  RoadmapItemSourceType,
} from "../../../../../../customer-experience/src/lib/ShiOneClient";
import {
  getCategoryDisplayLabel,
  getOrderedCategories,
} from "../../utils/azureRecommendationsCategoryMapping";
import { AzureListItemAdditionalContent } from "../../components/azure/AzureListItemAdditionalContent";

const azureAdapter: RecommendationAdapter<AzureRecommendationSummaryDto> = {
  normalize: (rec: AzureRecommendationSummaryDto): NormalizedRecommendation => {
    const searchableFields = [
      rec.category,
      rec.impact,
      rec.problem,
      rec.solution,
      ...(rec.affectedSubscriptions?.flatMap(
        (sub) =>
          sub.affectedResources?.map((r) =>
            [r.resourceName, r.resourceType].filter(Boolean).join(" "),
          ) || [],
      ) || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return {
      id: rec.recommendationTypeId || "",
      sourceId: `azure:${rec.category}:${rec.recommendationTypeId}:${rec.accountId || "unknown"}`,
      sourceType: "azure",
      title: rec.problem || "",
      description: rec.solution || "",
      category: rec.category || "Uncategorized",
      searchableText: searchableFields,
      metadata: {
        impact: rec.impact,
        totalAffectedSubscriptions: rec.totalAffectedSubscriptions,
        totalAffectedResources: rec.totalAffectedResources,
        accountId: rec.accountId,
      },
      originalData: rec,
      isLinkedToRoadmap: rec.isLinkedToRoadmap || false,
    };
  },

  getSourceId: (rec: AzureRecommendationSummaryDto) =>
    `azure:${rec.category}:${rec.recommendationTypeId}:${rec.accountId}`,

  getTitle: (rec: AzureRecommendationSummaryDto) =>
    rec.problem || rec.solution || "Azure Recommendation",

  getDescription: (rec: AzureRecommendationSummaryDto) =>
    rec.solution || rec.problem || "",
};

export const azureRecommendationHandler: RecommendationTypeHandler<AzureRecommendationSummaryDto> =
  {
    sourceType: RoadmapItemSourceType.AzureCloudRecommendation,

    displayConfig: {
      icon: CloudIcon,
      iconColor: "text.secondary",
      label: "Azure Recommendation",
      showDescription: false,
    },

    adapter: azureAdapter,

    detailDialog: AzureRecommendationDetailsDialog,

    createMetadata: (recommendation: AzureRecommendationSummaryDto) => {
      const azureRecommendationData = {
        id: `azure:${recommendation.category}:${recommendation.recommendationTypeId}:${recommendation.accountId}`,
        recommendationId: (recommendation as any).recommendationId, // Include the GUID for linking
        recommendationTypeId: recommendation.recommendationTypeId,
        accountId: recommendation.accountId,
        category: recommendation.category,
        impact: recommendation.impact,
        problem: recommendation.problem,
        solution: recommendation.solution,
        totalAffectedSubscriptions: recommendation.totalAffectedSubscriptions,
        totalAffectedResources: recommendation.totalAffectedResources,
        affectedSubscriptions: recommendation.affectedSubscriptions,
        // Include all affected resources so we can reference them later
        aggregatedRecommendation: true,
      };

      return {
        AzureRecommendation: JSON.stringify(azureRecommendationData),
      };
    },

    isType: (
      recommendation: any,
    ): recommendation is AzureRecommendationSummaryDto => {
      // Check if it's an Azure recommendation from the Recommendations API
      // or from the direct Azure API
      return (
        recommendation?.constructor?.name === "AzureRecommendationSummaryDto" ||
        (("recommendationTypeId" in recommendation ||
          "recommendationId" in recommendation) &&
          "category" in recommendation &&
          recommendation?.sourceType !==
            RoadmapItemSourceType.AwsCloudRecommendation)
      );
    },

    sourceSection: {
      sectionLabel: "AZURE RECOMMENDATIONS",
      getSources: (recommendations: AzureRecommendationSummaryDto[]) => {
        const categoryCounts = recommendations.reduce(
          (acc, rec) => {
            const category = rec.category || "";
            acc[category] = (acc[category] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );

        return getOrderedCategories()
          .filter((category) => categoryCounts[category] > 0)
          .map((category) => ({
            id: category,
            label: getCategoryDisplayLabel(category),
            secondaryLabel: `${categoryCounts[category]} Recommendation${
              categoryCounts[category] !== 1 ? "s" : ""
            }`,
            count: categoryCounts[category],
          }));
      },
      getSourceItems: (
        recommendations: AzureRecommendationSummaryDto[],
        sourceId: string,
      ) => {
        return recommendations.filter((rec) => rec.category === sourceId);
      },
    },

    getListItemContent: (
      recommendation: AzureRecommendationSummaryDto,
    ): ListItemContent => {
      const subscriptionCount = recommendation.totalAffectedSubscriptions || 0;
      const resourceCount = recommendation.totalAffectedResources || 0;
      const categoryLabel = getCategoryDisplayLabel(
        recommendation.category || "",
      );
      const baseText = `${categoryLabel} - ${recommendation.impact || ""}`;

      const counts = [];
      if (subscriptionCount > 0) {
        counts.push(
          `${subscriptionCount} subscription${subscriptionCount !== 1 ? "s" : ""}`,
        );
      }
      if (resourceCount > 0) {
        counts.push(
          `${resourceCount} resource${resourceCount !== 1 ? "s" : ""}`,
        );
      }

      const secondary =
        counts.length > 0
          ? `${baseText} (${counts.join(", ")} affected)`
          : baseText;

      return {
        primary:
          recommendation.problem ||
          recommendation.solution ||
          "Azure Recommendation",
        secondary,
      };
    },

    getChipLabel: (recommendation: AzureRecommendationSummaryDto): string => {
      const label =
        recommendation.problem ||
        recommendation.solution ||
        "Azure Recommendation";
      const subscriptionCount = recommendation.totalAffectedSubscriptions || 0;
      const resourceCount = recommendation.totalAffectedResources || 0;

      if (subscriptionCount > 0 || resourceCount > 0) {
        const parts = [];
        if (subscriptionCount > 0) {
          parts.push(
            `${subscriptionCount} sub${subscriptionCount !== 1 ? "s" : ""}`,
          );
        }
        if (resourceCount > 0) {
          parts.push(`${resourceCount} res`);
        }
        return `${label} (${parts.join(", ")})`;
      }

      return label;
    },

    getGroupKey: (recommendation: AzureRecommendationSummaryDto): string => {
      return `azure-${recommendation.category || "uncategorized"}`;
    },

    getGroupTitle: (groupKey: string): string => {
      if (groupKey.startsWith("azure-")) {
        const category = groupKey.replace("azure-", "");
        return `Azure - ${getCategoryDisplayLabel(category)}`;
      }
      return groupKey;
    },

    ListItemAdditionalContent: AzureListItemAdditionalContent,
  };
